import { RequireAccess } from '@/components/require-access'
import { Button } from '@/components/ui/button'
import { roleEnum, UserData } from '@jobilla/nexus-sdk'
import { TCell, TRow } from '@jobilla/starfield'
import { Trash2Icon } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { WidgetSelectDropdown } from '../../../../rapid-ui'
import { OrganizationMembersTableProps } from './organization-members-table'

function UserAvatar({ src, name }: { src: string | null; name: string | null }) {
	const { t } = useTranslation('common')
	const [imageError, setImageError] = useState(false)

	if (imageError || !src) {
		return (
			<div className="w-12 h-12 rounded-full bg-gray-200 text-gray-500 flex items-center justify-center text-sm font-medium">
				{name ? name.charAt(0).toUpperCase() : 'J'}
			</div>
		)
	}

	return (
		<img
			src={src}
			alt={name ?? t('organization.membersTable.userAvatar')}
			onError={() => setImageError(true)}
			className="w-12 h-12 rounded-full object-cover"
		/>
	)
}

interface OrganisationMemberRowProps {
	user: UserData
	isJobillan: boolean
	isCurrentUser: boolean
	onRoleChange: OrganizationMembersTableProps['onRoleChange']
	onRemoveClick: OrganizationMembersTableProps['onRemoveClick']
}

export function OrganisationMemberRow({
	user,
	isJobillan,
	isCurrentUser,
	onRoleChange,
	onRemoveClick,
}: OrganisationMemberRowProps) {
	const { t } = useTranslation('common')

	return (
		<TRow key={user.userId}>
			<TCell>
				<div className={'flex items-center gap-x-4'}>
					<div className={'flex-shrink-0'}>
						<UserAvatar src={user.avatarUrl} name={user.name} />
					</div>
					<div className={'gap-y-1.5 flex flex-col'}>
						<div className={'text- font-semibold'}>
							{user.name}
						</div>
						<div className={'text-sm text-gray-500 bg-gray-100 py-1 px-1 rounded'}>
							{user.email}
						</div>
					</div>
				</div>
			</TCell>
			<TCell>
				{new Date(user.joinedAt).toLocaleDateString(undefined, {
					year: 'numeric',
					month: 'long',
					day: 'numeric',
				})}
			</TCell>
			<TCell>
				<WidgetSelectDropdown
					options={[
						roleEnum.admin,
						roleEnum.member,
						roleEnum.recruiter,
						isJobillan ? roleEnum.jobillan : null,
					]
						.filter(Boolean).map(role => ({
							label: t(`organization.inviteDialog.role${role!.charAt(0).toUpperCase() + role!.slice(1)}`),
							id: role!,
						}))}
					value={user.role}
					onChange={(value) => onRoleChange(value, user.userId)}
					label={t('organization.membersTable.selectRole')}
				/>
			</TCell>
			<RequireAccess permission="organisation:remove-member">
				<TCell>
					{!isCurrentUser && (
						<Button
							variant="ghost"
							size="sm"
							onClick={() => onRemoveClick(user.userId, user.name ?? user.email ?? '', user.email ?? '')}
							className="text-red-700 hover:bg-red-50 hover:text-red-800"
						>
							<Trash2Icon className="size-3.5" aria-hidden="true" />
							{t('organization.membersTable.remove')}
						</Button>
					)}
				</TCell>
			</RequireAccess>
		</TRow>
	)
}
